import { useRef, useState } from 'react';

import { deleteRequest, get, post } from 'helpers/api';
import { isInlineToolCall } from './tools/inline';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';

import type { ToolCall } from './tools/types';
import type {
  ActionVerdict, Agent, AssistantAction, AssistantActionStatus, InputMode, Message, PageContext, Paths, StreamFrame,
} from './types';

interface AskResult {
  toolCalls?: ToolCall[];
  error?: boolean;
}

const isTerminal = (frame: StreamFrame) => frame.type === 'done' || frame.type === 'error';

function getPageContext(): PageContext {
  return { path: window.location.pathname };
}

// A resumed message carries the same undifferentiated tool calls a live turn streams, so the
// inline ones are picked out here rather than by the backend.
function toResumedMessage({ toolCalls, ...message }: Message & { toolCalls?: ToolCall[] }): Message {
  return { ...message, inlineToolCalls: toolCalls?.filter(isInlineToolCall) };
}

export function useAssistant(agents: Agent[] | null | undefined, paths: Paths) {
  const { subscribe, unsubscribe } = useOneOffSubscription();

  const [agent, setAgent] = useState((agents && agents[0]) || null);
  const [model, setModel] = useState(agent?.models.find(({ id }) => id === agent.model) || null);
  const [chatId, setChatId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  // Sending context only when it changes means the backend never has to de-duplicate it
  const sentPageContext = useRef<string | null>(null);

  async function loadChat(): Promise<{ chatId: number; messages: Message[] } | null> {
    const response = await get(paths.chat);
    if (!response?.chatId) return null;

    return { chatId: response.chatId, messages: (response.messages ?? []).map(toResumedMessage) };
  }

  async function resetChat() {
    const dismissedChatId = chatId;
    setChatId(null);
    sentPageContext.current = null;

    try {
      await deleteRequest(paths.chat, dismissedChatId != null ? { params: { chatId: dismissedChatId } } : {});
    } catch {
      // Dismissal is best-effort; on failure the old chat is offered for resume again.
    }
  }

  async function ask({ attachments, inputMode, prompt, onFrame }: {
    attachments?: File[],
    inputMode: InputMode,
    prompt: string,
    onFrame: (frame: StreamFrame) => void
  }) {
    function buildAskFormData(requestId: string) {
      const form = new FormData();

      form.append('prompt', prompt);
      form.append('request_id', requestId);
      form.append('input_mode', inputMode);
      attachments?.forEach(attachment => form.append('attachments[]', attachment));

      const pageContext = getPageContext();
      const serializedPageContext = JSON.stringify(pageContext);

      if (serializedPageContext !== sentPageContext.current) {
        sentPageContext.current = serializedPageContext;
        Object.entries(pageContext).forEach(([key, value]) => form.append(`page_context[${key}]`, value));
      }

      if (agent?.name) {
        form.append('agent', agent.name);
      }

      if (chatId != null) {
        form.append('chat_id', String(chatId));
      }

      if (model?.id) {
        form.append('model', model.id);
      }

      return form;
    }

    return new Promise<AskResult>((resolve) => {
      setLoading(true);
      function finish(result: AskResult) {
        setLoading(false);
        resolve(result);
      }

      subscribe<StreamFrame>(
        {
          async connected(requestId: string) {
            const response = await post(paths.ask, buildAskFormData(requestId));
            setChatId(response.chatId);
          },
          connectedError() {
            onFrame({ type: 'error', error: true });
            finish({ error: true });
          },
          received(frame) {
            onFrame(frame);
            if (frame.type === 'done') finish({ toolCalls: frame.toolCalls });
            else if (frame.type === 'error') finish({ error: true });
          },
        },
        { isTerminal }
      );
    });
  }

  async function resolveAction({ action, verdict, onResolved, onFrame }: {
    action: AssistantAction,
    verdict: ActionVerdict,
    onResolved: (status: AssistantActionStatus, resumed: boolean) => void,
    onFrame: (frame: StreamFrame) => void
  }) {
    const path = verdict === 'apply' ? action.applyPath : action.rejectPath;

    return new Promise<AskResult>((resolve) => {
      setLoading(true);
      function finish(result: AskResult) {
        setLoading(false);
        resolve(result);
      }

      subscribe<StreamFrame>(
        {
          async connected(requestId: string) {
            const response = await post(path, { requestId, agent: agent?.name });
            onResolved(response.status, response.resumed);
            if (!response.resumed) {
              unsubscribe();
              finish({});
            }
          },
          connectedError() {
            onFrame({ type: 'error', error: true });
            finish({ error: true });
          },
          received(frame) {
            onFrame(frame);
            if (frame.type === 'done') finish({ toolCalls: frame.toolCalls });
            else if (frame.type === 'error') finish({ error: true });
          },
        },
        { isTerminal }
      );
    });
  }

  return {
    agent,
    ask,
    loadChat,
    loading,
    model,
    resetChat,
    resolveAction,
    setAgent,
    setChatId,
    setModel,
  };
}
